import type { NutrientIndicator } from '../types'
import { useTranslation } from '../i18n/LanguageContext'

interface NutrientProgressBarProps {
  indicator: NutrientIndicator
  height?: number
}

interface ProgressSection {
  start: number
  end: number
  color: string
  label: string
}

export function NutrientProgressBar({ indicator, height = 12 }: NutrientProgressBarProps) {
  const { t } = useTranslation()
  const { value, min, max, target, algorithm } = indicator

  if (algorithm === 'ignore') {
    return (
      <div
        className="position-relative"
        style={{ height: `${height}px` }}
      >
        <div
          className="w-100 h-100 rounded"
          style={{
            background: '#ced4da',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
          }}
        />
      </div>
    )
  }

  const result = calculateProgressSections(value, min, target, max, t)
  const sections = result.sections
  const currentValuePosition = result.valuePosition

  return (
    <div
      className="position-relative"
      style={{ height: `${height}px` }}
    >
      <div className="d-flex h-100 rounded overflow-hidden" style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)' }}>
        {sections.map((section, index) => (
          <div
            key={index}
            className="position-relative"
            style={{
              width: `${section.end - section.start}%`,
              background: section.color
            }}
            title={section.label}
          />
        ))}
      </div>

      <div
        className="position-absolute top-0 bottom-0 rounded"
        style={{
          left: `${Math.max(0, Math.min(100, currentValuePosition))}%`,
          width: '4px',
          background: indicator.passing ? '#155724' : '#721c24',
          transform: 'translateX(-50%)',
          zIndex: 10,
          boxShadow: '0 0 3px rgba(0,0,0,0.3)'
        }}
      />
    </div>
  )
}

type TFn = (key: string, vars?: Record<string, string | number>) => string

function calculateProgressSections(
  value: number,
  min: number | undefined,
  target: number | undefined,
  max: number | undefined,
  t: TFn
): { sections: ProgressSection[]; valuePosition: number } {
  const sections: ProgressSection[] = []

  const hasMin = min !== undefined
  const hasMax = max !== undefined
  const hasTarget = target !== undefined

  let rangeMin = 0
  let rangeMax = 100

  if (hasMax) {
    rangeMax = max * 1.2
    if (hasMin) {
      rangeMin = Math.min(0, min * 0.1)
    }
  } else if (hasMin) {
    rangeMax = min * 5
    rangeMin = Math.min(0, min * 0.1)
  } else if (hasTarget) {
    rangeMax = target * 2
    rangeMin = Math.min(0, target * 0.1)
  }

  if (value > rangeMax * 0.9) {
    rangeMax = value * 1.2
  }
  if (value < rangeMin) {
    rangeMin = value * 1.2
  }

  if (rangeMax <= rangeMin) {
    rangeMax = rangeMin + (Math.abs(rangeMin) || 100)
  }

  const totalRange = rangeMax - rangeMin
  const toPercent = (val: number) => ((val - rangeMin) / totalRange) * 100

  let currentPos = 0

  if (hasMin && hasMax) {
    const minPos = toPercent(min)
    const maxPos = toPercent(max)
    const inOptimalRange = value >= min && value <= max

    if (minPos > 0) {
      sections.push({
        start: currentPos,
        end: minPos,
        color: inOptimalRange ? '#efe6e8' : '#ff5252',
        label: t('progress.belowMin', { value: min })
      })
      currentPos = minPos
    }

    sections.push({
      start: currentPos,
      end: maxPos,
      color: inOptimalRange ? '#a0dc38' : '#98d978',
      label: t('progress.optimalRange', { min, max })
    })
    currentPos = maxPos

    if (currentPos < 100) {
      sections.push({
        start: currentPos,
        end: 100,
        color: inOptimalRange ? '#efe6e8' : '#ff5252',
        label: t('progress.aboveMax', { value: max })
      })
    }

  } else if (hasMin && !hasMax) {
    const minPos = toPercent(min)
    const isAboveMin = value >= min

    if (minPos > 0) {
      sections.push({
        start: currentPos,
        end: minPos,
        color: isAboveMin ? '#efe6e8' : '#ff5252',
        label: t('progress.belowMin', { value: min })
      })
      currentPos = minPos
    }

    if (currentPos < 100) {
      sections.push({
        start: currentPos,
        end: 100,
        color: isAboveMin ? '#a0dc38' : '#98d978',
        label: t('progress.aboveMin', { value: min })
      })
    }

  } else if (!hasMin && hasMax) {
    const maxPos = toPercent(max)

    sections.push({
      start: currentPos,
      end: maxPos,
      color: '#98d978',
      label: t('progress.belowMax', { value: max })
    })
    currentPos = maxPos

    const isAchieved = value <= max
    if (currentPos < 100) {
      sections.push({
        start: currentPos,
        end: 100,
        color: isAchieved ? '#efe6e8' : '#ff5252',
        label: t('progress.aboveMax', { value: max })
      })
    }

  } else {
    sections.push({
      start: 0,
      end: 100,
      color: '#495057',
      label: t('progress.noConstraint')
    })
  }

  const valuePosition = ((value - rangeMin) / totalRange) * 100

  return { sections, valuePosition }
}
